/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {PersonType} from "types/enums/PersonType";
import {MinusIcon, PlusIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import {toast} from "react-toastify";
import {UseFormSetValue} from "react-hook-form/dist/types";
import {BookingFilter} from "types/app";
import {Package} from "types/entities/Package";
import {useTypedSelector} from "helpers/reducers";
import DriverCountPersonType from "pages/Front/Booking/components/BookingFilters/DriverCountPersonType";
import {getDriversCountByCategory} from "helpers/helpers";
import {PackageCategory} from "types/enums/PackageCategory";

interface DriversCountFilterItemNewProps {
    name: "children" | "adults" | "date" | "activity_ids" | "groupType";
    personType: PersonType;
    setValue: UseFormSetValue<BookingFilter>;
    register: any;
    isMinimalistic: boolean;
    watch: any;
    onChange: (type: string, value: number) => void;
    disabled?: boolean;
    isGiftCard?: boolean;
    isGroupTypeChildren?: boolean;
    isEvent?: boolean;
    isAction?: boolean;
    selectedPackage: Package | undefined | null;
    className?: string;
}

const DriversCountFilterItemNew = (props: DriversCountFilterItemNewProps) => {
    const {t} = useTranslation();
    const valueButtonClassName =
        (props.disabled ? "bg-gray-700 cursor-not-allowed" : "bg-arbo-orange text-white cursor-pointer") +
        "  flex-grow text-center flex flex-row justify-center items-center min-w-[40px] sm:min-w-[40px] border border-arbo-orange";
    // const [driversCount, setDriversCount] = useState<number>(0);
    const maxValue = 1000;
    const driversCount = props.watch(props.name);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);

    const minimumDriversCount = bookingFilter?.category ? getDriversCountByCategory(bookingFilter.category)[props.personType].minimum : 0;
    let maximumDriversCount = bookingFilter?.category ? getDriversCountByCategory(bookingFilter.category)[props.personType].maximum : maxValue;
    const packageMaximum = bookingFilter?.category ? getDriversCountByCategory(bookingFilter.category)["package"].maximum : undefined;

    const setMaximumDriversForMegaSchool = () => {
        if ([PackageCategory.MegaFunSchool, PackageCategory.SchoolTrophy].includes(bookingFilter.category as PackageCategory) && props.personType == "adult") {
            return (maximumDriversCount = Math.round(bookingFilter.children * 0.25));
        }
        return maximumDriversCount;
    };

    const setMinimumDriversForMegaSchool = () => {
        if ([PackageCategory.MegaFunSchool, PackageCategory.SchoolTrophy].includes(bookingFilter.category as PackageCategory) && props.personType == "child") {
            let optimumDrivers: number = getDriversCountByCategory(bookingFilter.category as PackageCategory)[props.personType].optimum as number;
            if (bookingFilter.adults + bookingFilter.children < getDriversCountByCategory(bookingFilter.category as PackageCategory)["package"].minimum) {
                setFilterValue(optimumDrivers);
            }
        }
    };

    const setDriversCountHandlerForMegaSchool = () => {
        if ([PackageCategory.MegaFunSchool, PackageCategory.SchoolTrophy].includes(bookingFilter.category as PackageCategory) && props.personType == "adult") {
            const maxDrivers = setMaximumDriversForMegaSchool();
            if (driversCount > maxDrivers) {
                toast.warning(t("cannot_take_more_persons_megafun_school", {count: maxDrivers}), {toastId: "cannot_take_more_persons_megafun_school"});
                setFilterValue(maxDrivers);
            }
        }
    };

    const setDriversCountFromKeyPress = (keyCode: string) => {
        switch (keyCode) {
            case "ArrowUp":
                return setDriversCountHandler(+1);
            case "ArrowDown":
                return setDriversCountHandler(-1);
            default:
                return;
        }
    };
    const setDriversCountValue = (driversCountValue, withCheck = true) => {
        if (isNaN(+driversCountValue)) {
            toast.warning(t("Enter numbers only"));
            return;
        }
        if (!withCheck) {
            return setFilterValue(+driversCountValue);
        }
        checkNewFilterValue(+driversCountValue);
    };

    const setDriversCountHandler = (newDriversCount: number) => {
        const newCount = +driversCount + newDriversCount;
        checkNewFilterValue(newCount);
    };

    const checkNewFilterValue = (value: number) => {
        const restValue = props.personType === PersonType.Child ? bookingFilter.adults : bookingFilter.children;
        if (packageMaximum) {
            if (restValue + value > packageMaximum) {
                toast.warning(t("cannot_take_more_persons", {count: packageMaximum}), {toastId: "cannot_take_more_persons"});
                return;
            }
        }

        if (maximumDriversCount && value > maximumDriversCount) {
            toast.warning(t("cannot_take_more_persons", {count: packageMaximum}), {toastId: "cannot_take_more_persons"});
            return;
        }

        //if new count is equal to zero, set it
        if (value < 0 || value === 0) {
            setFilterValue(0);
            return;
        }

        //if new count is less than minimum, set minimum
        if (value < minimumDriversCount) {
            setFilterValue(minimumDriversCount);
            toast.warning(t("cannot_take_less_persons", {count: minimumDriversCount}), {toastId: "cannot_take_more_persons"});
            return;
        }

        //otherwise if new count is more than zero, set it
        if (value >= 0) {
            toast.dismiss("cannot_take_more_persons");
            setFilterValue(value);
        }
    };

    const setFilterValue = (value: number) => {
        props.setValue(props.name, value);
        props.onChange(props.name, value);
    };

    useEffect(() => {
        if (minimumDriversCount > 0 && driversCount === 0) {
            setDriversCountHandler(minimumDriversCount);
        }
    }, [driversCount]);

    useEffect(() => {
        setMinimumDriversForMegaSchool();
        setMaximumDriversForMegaSchool();
        setDriversCountHandlerForMegaSchool();
    }, [bookingFilter.children, bookingFilter.adults]);

    return (
        <div className={"DriversCountFilterItemNew"}>
            <div
                className={clsx(
                    props.disabled && "bg-opacity-50 opacity-50 grayscale cursor-not-allowed pointer-events-none select-none",
                    "flex flex-col sm:flex-row justify-between items-center space-x-2 xl:space-x-4",
                    props.className
                )}
            >
                {props.isGiftCard ? (
                    <div className={"flex flex-col w-1/2 md:w-1/2"}>
                        <div className={"capitalize "}>
                            <b>{t("Gift cards")}</b>
                        </div>
                    </div>
                ) : (
                    <DriverCountPersonType
                        classNameTitle={"mb-1 !font-semibold"}
                        isAction={props.isAction}
                        isEvent={props.isEvent}
                        isGroupTypeChildren={props.isGroupTypeChildren}
                        isMinimalistic={props.isMinimalistic}
                        minimumDriversCount={minimumDriversCount}
                        personType={props.personType}
                    />
                )}

                <div
                    className={clsx(
                        props.disabled ? "border-gray-800" : "bg-arbo-orange",
                        "flex flex-row justify-between items-stretch h-10 w-1/2 md:w-1/2 max-w-[144px] rounded-md overflow-hidden"
                    )}
                >
                    <button
                        className={clsx(valueButtonClassName, "rounded-tl-md rounded-bl-md")}
                        id={"DecrementPersonsCount-" + props.personType}
                        type={"button"}
                        onClick={() => setDriversCountHandler(-1)}
                    >
                        <MinusIcon className={"w-5 h-5 flex-none"} />
                    </button>
                    {/*<div className={"bg-white text-xl text-gray-800 leading-4 text-center flex flex-row justify-center items-center"}>*/}
                    <div
                        className={
                            "bg-white text-base text-gray-800 leading-4 text-center flex flex-row justify-center items-center border-t border-b border-gray-200 "
                        }
                    >
                        <input
                            className={clsx(
                                "shrink w-full px-2 text-center h-full text-black hover:outline-none hover:appearance-none",
                                props?.name && "CountFilter-" + props.name
                            )}
                            inputMode={"numeric"}
                            max={maximumDriversCount}
                            min={minimumDriversCount}
                            style={{
                                appearance: "textfield",
                                WebkitAppearance: "none",
                                MozAppearance: "textfield",
                            }}
                            type={"text"}
                            value={driversCount}
                            onChange={e => setDriversCountValue(e.target.value, false)}
                            onBlur={e => setDriversCountValue(e.target.value)}
                            onKeyDown={e => setDriversCountFromKeyPress(e.key)}
                        />
                    </div>
                    <input {...props.register(props.name)} name={props.name} type="hidden" />
                    <button
                        className={clsx(valueButtonClassName, "rounded-tr-md rounded-br-md")}
                        id={"IncrementPersonsCount-" + props.personType}
                        type={"button"}
                        onClick={() => setDriversCountHandler(+1)}
                    >
                        <PlusIcon className={"w-5 h-5 flex-none"} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DriversCountFilterItemNew;
